import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircle, Circle, Clock, ChevronRight, ChevronDown, ArrowUpDown,
  User as UserIcon, MessageSquare, Send, Trash2, Plus, X, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const PRIORITIES = [
  { key: 'high',   label: 'High',   rank: 0, dot: 'bg-red-500',    chip: 'bg-red-100 text-red-800' },
  { key: 'normal', label: 'Medium', rank: 1, dot: 'bg-amber-500',  chip: 'bg-amber-100 text-amber-800' },
  { key: 'low',    label: 'Low',    rank: 2, dot: 'bg-slate-400',  chip: 'bg-slate-100 text-slate-700' },
];
const priorityOf = (k) => PRIORITIES.find(p => p.key === k) || PRIORITIES[1];

const STATUS_OPTIONS = [
  { key: 'open',        label: 'Open' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'blocked',     label: 'Blocked' },
  { key: 'done',        label: 'Done' },
];

function fmtRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('open');  // all / open / done / mine
  const [groupBy, setGroupBy] = useState('priority'); // priority | property | none
  const [openDetail, setOpenDetail] = useState(null); // task obj
  const [creating, setCreating] = useState(false);

  const load = () => Promise.all([
    api.get('/tasks'),
    api.get('/properties'),
    api.get('/users').catch(() => ({ data: [] })),
  ]).then(([t, p, u]) => { setTasks(t.data); setProps(p.data); setUsers(u.data || []); });
  useEffect(() => { load(); }, []);

  const complete = async (id) => { await api.post(`/tasks/${id}/complete`); load(); };
  const updateTask = async (id, patch) => {
    const r = await api.patch(`/tasks/${id}`, patch);
    // refresh in place + drawer
    setTasks(prev => prev.map(t => t.id === id ? r.data : t));
    if (openDetail?.id === id) setOpenDetail(r.data);
  };

  const propMap = useMemo(() => Object.fromEntries(props.map(p => [p.id, p])), [props]);

  const visible = tasks.filter(t => {
    if (filter === 'mine') return t.assignee_user_id === user?.user_id;
    if (filter === 'open') return t.status !== 'done';
    if (filter === 'done') return t.status === 'done';
    return true;
  });

  const openCount = tasks.filter(t => t.status !== 'done').length;
  const doneCount = tasks.filter(t => t.status === 'done').length;
  const myCount = tasks.filter(t => t.assignee_user_id === user?.user_id && t.status !== 'done').length;

  // Grouping
  const grouped = useMemo(() => {
    if (groupBy === 'priority') {
      return PRIORITIES.map(p => ({
        key: p.key,
        title: p.label + ' priority',
        chip: p.chip,
        dot: p.dot,
        items: visible.filter(t => (t.priority || 'normal') === p.key)
                      .sort((a,b) => (a.due_date || '') > (b.due_date || '') ? 1 : -1),
      }));
    }
    if (groupBy === 'property') {
      const byId = {};
      for (const t of visible) {
        const key = t.property_id || 'unassigned';
        byId[key] = byId[key] || { key, title: propMap[key]?.title || (key === 'unassigned' ? 'No property' : 'Unknown property'), items: [] };
        byId[key].items.push(t);
      }
      // sort each group by priority then due
      Object.values(byId).forEach(g => g.items.sort((a,b) =>
        priorityOf(a.priority).rank - priorityOf(b.priority).rank ||
        ((a.due_date || '') > (b.due_date || '') ? 1 : -1)
      ));
      return Object.values(byId);
    }
    // none: single flat, sorted by priority then due
    return [{
      key: 'all',
      title: 'All tasks',
      items: [...visible].sort((a,b) =>
        priorityOf(a.priority).rank - priorityOf(b.priority).rank ||
        ((a.due_date || '') > (b.due_date || '') ? 1 : -1)
      ),
    }];
  }, [visible, groupBy, propMap]);

  return (
    <div className="py-6 max-w-5xl mx-auto space-y-6" data-testid="tasks-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Workflow</div>
          <h1 className="font-serif text-4xl">Tasks</h1>
          <p className="text-slate-600 mt-2 text-sm">
            {openCount} open · {doneCount} done{myCount > 0 && <> · <span className="text-[#679286] font-medium">{myCount} assigned to you</span></>}
          </p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-task-btn" className="btn-primary text-sm flex items-center gap-2">
          <Plus size={14}/> New task
        </button>
      </div>

      {/* Filter + Group controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="glass p-1.5 inline-flex gap-1">
          {[
            { k: 'open', l: 'Open' },
            { k: 'mine', l: 'Assigned to me' },
            { k: 'done', l: 'Done' },
            { k: 'all',  l: 'All' },
          ].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`px-4 py-1.5 rounded-full text-sm ${filter === f.k ? 'bg-[#1A2530] text-white' : 'text-slate-600 hover:bg-white/60'}`}>{f.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <ArrowUpDown size={13} className="text-slate-400"/>
          <span className="text-slate-500">Group by</span>
          <div className="glass p-1 inline-flex gap-1">
            {[
              { k: 'priority', l: 'Priority' },
              { k: 'property', l: 'Property' },
              { k: 'none',     l: 'None' },
            ].map(g => (
              <button key={g.k} onClick={() => setGroupBy(g.k)}
                data-testid={`group-${g.k}`}
                className={`px-3 py-1 rounded-full text-xs ${groupBy === g.k ? 'bg-[#679286] text-white' : 'text-slate-600 hover:bg-white/60'}`}>
                {g.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grouped list */}
      {grouped.filter(g => g.items.length > 0).length === 0 && (
        <div className="glass p-12 text-center text-slate-400">No tasks match this filter.</div>
      )}

      {grouped.filter(g => g.items.length > 0).map(g => (
        <TaskGroup key={g.key} group={g} onOpen={setOpenDetail} onComplete={complete} propMap={propMap} groupBy={groupBy}/>
      ))}

      {/* Detail drawer */}
      <AnimatePresence>
        {openDetail && (
          <TaskDetail
            task={openDetail}
            users={users}
            propMap={propMap}
            onClose={() => setOpenDetail(null)}
            onUpdate={updateTask}
            onReload={load}
            currentUser={user}
          />
        )}
      </AnimatePresence>

      {creating && (
        <TaskCreateModal
          users={users} props={props}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Grouped list ----------
function TaskGroup({ group, onOpen, onComplete, propMap, groupBy }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="glass p-5" data-testid={`task-group-${group.key}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 mb-3"
      >
        {open ? <ChevronDown size={18} className="text-slate-400"/> : <ChevronRight size={18} className="text-slate-400"/>}
        {group.dot && <span className={`w-2.5 h-2.5 rounded-full ${group.dot}`}/>}
        <h3 className="font-serif text-xl text-slate-900">{group.title}</h3>
        <span className="text-xs text-slate-400 font-medium">{group.items.length}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-1">
              {group.items.map(t => (
                <TaskRow key={t.id} t={t} onOpen={onOpen} onComplete={onComplete} propMap={propMap} showProperty={groupBy !== 'property'}/>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TaskRow({ t, onOpen, onComplete, propMap, showProperty }) {
  const prop = propMap[t.property_id];
  const p = priorityOf(t.priority);
  const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== 'done';
  return (
    <div
      onClick={() => onOpen(t)}
      className="flex items-start gap-3 py-2.5 px-2 -mx-2 rounded-xl hover:bg-white/70 cursor-pointer transition-colors border-b border-slate-100/60 last:border-b-0"
      data-testid={`task-${t.id}`}
    >
      <button
        onClick={(e) => { e.stopPropagation(); if (t.status !== 'done') onComplete(t.id); }}
        className="text-slate-400 hover:text-emerald-600 mt-0.5 flex-shrink-0"
        data-testid={`task-complete-${t.id}`}
      >
        {t.status === 'done' ? <CheckCircle size={18} className="text-emerald-600" /> : <Circle size={18}/>}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${t.status === 'done' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{t.title}</div>
        <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
          {t.due_date && (
            <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : ''}`}>
              {overdue ? <AlertCircle size={11}/> : <Clock size={11}/>} {new Date(t.due_date).toLocaleDateString()}
            </span>
          )}
          {t.assignee_name && (
            <span className="flex items-center gap-1"><UserIcon size={11}/> {t.assignee_name}</span>
          )}
          {t.assigned_by_name && t.assigned_by_name !== t.assignee_name && (
            <span className="text-slate-400">by {t.assigned_by_name}</span>
          )}
          {(t.notes?.length || 0) > 0 && (
            <span className="flex items-center gap-1 text-slate-400"><MessageSquare size={11}/> {t.notes.length}</span>
          )}
          {showProperty && prop && (
            <Link
              onClick={(e) => e.stopPropagation()}
              to={`/app/properties/${prop.id}`}
              className="text-[#679286] hover:underline truncate max-w-[220px]"
            >→ {prop.title}</Link>
          )}
        </div>
      </div>
      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${p.chip} flex-shrink-0`}>{p.label}</span>
    </div>
  );
}

// ---------- Detail drawer ----------
function TaskDetail({ task, users, propMap, onClose, onUpdate, onReload, currentUser }) {
  const [newNote, setNewNote] = useState('');
  const [posting, setPosting] = useState(false);
  const prop = propMap[task.property_id];

  const post = async () => {
    if (!newNote.trim()) return;
    setPosting(true);
    try {
      const r = await api.post(`/tasks/${task.id}/notes`, { body: newNote.trim() });
      setNewNote('');
      onReload();
      // update local drawer
      task.notes = r.data.notes;
    } finally { setPosting(false); }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    await api.delete(`/tasks/${task.id}/notes/${noteId}`);
    onReload();
    task.notes = (task.notes || []).filter(n => n.id !== noteId);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.aside
        initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-full max-w-xl bg-white shadow-2xl overflow-y-auto"
        data-testid="task-detail-drawer"
      >
        <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-3 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
          <div className="flex-1 min-w-0">
            <div className="overline">Task</div>
            <h2 className="font-serif text-2xl mt-1">{task.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100" data-testid="task-detail-close"><X size={16}/></button>
        </div>

        <div className="p-6 space-y-5">
          {task.description && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{task.description}</p>
          )}

          {/* Meta grid */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs text-slate-500 mb-1">Status</div>
              <select
                value={task.status}
                onChange={(e) => onUpdate(task.id, { status: e.target.value, ...(e.target.value === 'done' ? { completed_at: new Date().toISOString(), completed_by_user_id: currentUser?.user_id } : {}) })}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="task-status-select"
              >
                {STATUS_OPTIONS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Priority</div>
              <select
                value={task.priority}
                onChange={(e) => onUpdate(task.id, { priority: e.target.value })}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="task-priority-select"
              >
                {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Assignee</div>
              <select
                value={task.assignee_user_id || ''}
                onChange={(e) => onUpdate(task.id, {
                  assignee_user_id: e.target.value || null,
                  assignee_name: users.find(u => u.user_id === e.target.value)?.name || null,
                })}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="task-assignee-select"
              >
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Due date</div>
              <input
                type="date"
                value={task.due_date ? task.due_date.slice(0, 10) : ''}
                onChange={(e) => onUpdate(task.id, { due_date: e.target.value || null })}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="task-due-input"
              />
            </div>
          </div>

          {/* Assigned-by trail */}
          <div className="text-xs text-slate-500 space-y-0.5 pt-2 border-t border-slate-100">
            {task.assigned_by_name && <div>Created by <b className="text-slate-700">{task.assigned_by_name}</b> · {fmtRelative(task.created_at)}</div>}
            {task.completed_at && <div className="text-emerald-700">Completed · {fmtRelative(task.completed_at)}</div>}
            {prop && <div>Property: <Link to={`/app/properties/${prop.id}`} className="text-[#679286] hover:underline">{prop.title}</Link></div>}
            {task.linked_type && task.linked_id && (
              <div>
                Linked to:{' '}
                <Link
                  to={
                    task.linked_type === 'invoice' ? `/app/approvals?open=${task.linked_id}` :
                    task.linked_type === 'snag' ? (prop ? `/app/properties/${prop.id}#snags` : '#') :
                    task.linked_type === 'drawing' ? `/app/drawings-permits?open=${task.linked_id}` :
                    task.linked_type === 'permit' ? `/app/drawings-permits?open=${task.linked_id}` :
                    task.linked_type === 'boq' ? `/app/boq?open=${task.linked_id}` :
                    task.linked_type === 'vendor' ? `/app/vendors?open=${task.linked_id}` : '#'
                  }
                  className="text-[#679286] hover:underline font-medium capitalize"
                  data-testid={`task-linked-${task.linked_type}`}
                >
                  {task.linked_type} · Open →
                </Link>
              </div>
            )}
          </div>

          {/* Progress notes */}
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare size={14} className="text-slate-500"/>
              <h3 className="font-serif text-lg">Progress notes</h3>
              <span className="text-xs text-slate-400">{task.notes?.length || 0}</span>
            </div>
            <div className="space-y-3 mb-4" data-testid="task-notes-list">
              {(!task.notes || task.notes.length === 0) && (
                <div className="text-sm text-slate-400 italic">No notes yet — leave the first daily update below.</div>
              )}
              {(task.notes || []).slice().reverse().map(n => (
                <div key={n.id} className="p-3 rounded-2xl bg-slate-50/80 border border-slate-100" data-testid={`task-note-${n.id}`}>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                    <span><b className="text-slate-700">{n.author_name || 'Someone'}</b> · {fmtRelative(n.created_at)}</span>
                    {(n.author_user_id === currentUser?.user_id || ['owner','md'].includes(currentUser?.role)) && (
                      <button onClick={() => removeNote(n.id)} className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`task-note-delete-${n.id}`}>
                        <Trash2 size={11}/>
                      </button>
                    )}
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <textarea
                rows={2}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); post(); } }}
                placeholder="Leave a progress note (Ctrl/Cmd+Enter to send)…"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="task-note-input"
              />
              <button
                onClick={post}
                disabled={posting || !newNote.trim()}
                className="btn-primary text-sm flex items-center gap-1.5 self-end disabled:opacity-40"
                data-testid="task-note-submit"
              >
                {posting ? '…' : <><Send size={13}/> Post</>}
              </button>
            </div>
          </div>
        </div>
      </motion.aside>
    </motion.div>
  );
}

// ---------- Create modal ----------
function TaskCreateModal({ users, props, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', priority: 'normal',
    assignee_user_id: '', property_id: '', due_date: '',
    linked_type: '', linked_id: '',
  });
  const [linkOptions, setLinkOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  // Load link candidates when linked_type or property_id changes
  useEffect(() => {
    if (!form.linked_type) { setLinkOptions([]); return; }
    const endpoint = {
      drawing: '/drawings', permit: '/permits', invoice: '/invoices',
      snag: '/snags', boq: '/boqs', vendor: '/vendors',
    }[form.linked_type];
    if (!endpoint) return;
    api.get(endpoint).then(r => {
      let items = r.data || [];
      if (form.property_id && form.linked_type !== 'vendor') {
        items = items.filter(i => i.property_id === form.property_id);
      }
      setLinkOptions(items);
    }).catch(() => setLinkOptions([]));
  }, [form.linked_type, form.property_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      const payload = { ...form };
      ['assignee_user_id', 'property_id', 'due_date', 'linked_type', 'linked_id'].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      await api.post('/tasks', payload);
      toast.success('Task created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create task');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <motion.form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-lg p-8 max-h-[90vh] overflow-y-auto"
        data-testid="task-create-modal"
      >
        <div className="overline mb-1">New</div>
        <h2 className="font-serif text-2xl mb-5">Create task</h2>
        <div className="space-y-3 text-sm">
          <label className="block">Title
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
              data-testid="new-task-title"/>
          </label>
          <label className="block">Description
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
              data-testid="new-task-description"/>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">Priority
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="new-task-priority">
                {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
            <label className="block">Due date
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="new-task-due"/>
            </label>
            <label className="block">Assignee
              <select value={form.assignee_user_id} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="new-task-assignee">
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>)}
              </select>
            </label>
            <label className="block">Property
              <select value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value, linked_id: '' })}
                className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                data-testid="new-task-property">
                <option value="">None</option>
                {props.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </label>
          </div>

          {/* Deep-link picker */}
          <div className="pt-3 border-t border-white/60">
            <div className="overline mb-2">Link to (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">Type
                <select value={form.linked_type} onChange={(e) => setForm({ ...form, linked_type: e.target.value, linked_id: '' })}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm"
                  data-testid="new-task-link-type">
                  <option value="">None</option>
                  <option value="drawing">Drawing</option>
                  <option value="permit">Permit</option>
                  <option value="invoice">Invoice</option>
                  <option value="snag">Snag</option>
                  <option value="boq">BOQ</option>
                  <option value="vendor">Vendor</option>
                </select>
              </label>
              <label className="block">Item
                <select value={form.linked_id} onChange={(e) => setForm({ ...form, linked_id: e.target.value })}
                  disabled={!form.linked_type}
                  className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm disabled:opacity-50"
                  data-testid="new-task-link-id">
                  <option value="">{form.linked_type ? (linkOptions.length ? 'Select…' : 'No items') : '—'}</option>
                  {linkOptions.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.title || o.name || o.invoice_number || o.number || (o.id || '').slice(0, 10)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {form.linked_type && form.property_id && (
              <div className="text-[11px] text-slate-500 mt-2">Filtered by selected property. {linkOptions.length} match.</div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="new-task-submit">{busy ? 'Creating…' : 'Create task'}</button>
        </div>
      </motion.form>
    </div>
  );
}
